import React, { useState, useEffect } from 'react';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Text,
  DataTable,
  DataTableHeader,
  DataTableBody,
  DataTableRow,
  DataTableHead,
  DataTableCell,
  DataPagination,
} from "@musa.tech/design-system";
import { FileText, Download, AlertCircle, SquareArrowOutUpRight } from "lucide-react";
import { InvoiceStatusBadge } from "./InvoiceStatusBadge";
import { MeasurementStatusBadge } from "./contestacao/MeasurementStatusBadge";
import { contestationDisplayDeadline, formatDeadline } from "./contestacao/contestationWindow";
import type { Invoice, ReleaseStatus } from "@/integrations/faturamento/client";
import { TableSkeleton } from "./FinanceSkeletons";
import { useInvoices } from "@/hooks/useInvoices";
import { useInvoicesCSVExport } from "@/hooks/useInvoicesCSVExport";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useDemoMode } from "@/contexts/DemoModeContext";
import { maskGeneratorName, maskCNPJ, maskNumericValue } from "@/utils/demoMode";
import { openCloudHumansChat } from "@/utils/cloudHumans";

const ITEMS_PER_PAGE = 10;

interface OrdersTableProps {
  dueDateRange?: { start: Date | null; end: Date | null } | null;
  /** Competências específicas (`YYYY-MM`), não um intervalo — ver GR-670. */
  billingPeriods?: string[];
  generatorId?: number[];
  establishmentIds?: number[];
  statusFilter?: string;
  releaseStatuses?: string[];
  /** `'FATURA'` e/ou `'MEDICAO'`. Os dois juntos (ou vazio) têm o mesmo efeito de ausente. */
  types?: string[];
  onOpenDetail: (sfmId: string) => void;
}

/**
 * Medição e fatura são o mesmo ciclo em fases diferentes: enquanto está em análise interna não há
 * OS na Omie, logo não há número de pedido nem status de cobrança.
 */
function isInvoiced(invoice: Invoice): boolean {
  return !!invoice.os_code;
}

function formatCnpj(cnpj: string | null | undefined): string {
  if (!cnpj) return '';
  const digits = cnpj.replace(/\D/g, '');
  if (digits === cnpj) return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  return cnpj;
}

function formatDate(date: string | null | undefined): string {
  if (!date) return '';
  const [year, month, day] = date.split('-');
  if (!year || !month || !day) return date;
  return `${day}/${month}/${year}`;
}

function formatCompetencia(competencia: string | null | undefined): string {
  if (!competencia) return '';
  const [year, month] = competencia.split('-').map(Number);
  if (!year || !month) return '';
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${months[month - 1]}/${year}`;
}

/**
 * Mesma coluna "Vencimento", sem título novo. Fatura tem `due_date`; medição ainda não, e o
 * espaço vazio vira o prazo de contestação enquanto a janela de revisão está aberta — como uma
 * data simples, no mesmo formato da fatura, sem frase explicando o que ela é (decisão de
 * produto). Nunca as duas juntas: fatura nunca mostra prazo de contestação aqui (não evidenciar a
 * possibilidade de contestar algo já faturado).
 */
function VencimentoCell({ invoice }: { invoice: Invoice }) {
  const dueDateLabel = formatDate(invoice.due_date);
  const deadline = contestationDisplayDeadline({
    releaseStatus: invoice.release_status,
    releaseAt: invoice.release_at,
    isInvoiced: isInvoiced(invoice),
  });

  const label = dueDateLabel || (deadline ? formatDeadline(deadline) : '');
  if (!label) return <>—</>;

  return <span>{label}</span>;
}

export const OrdersTable: React.FC<OrdersTableProps> = ({
  dueDateRange,
  billingPeriods,
  generatorId,
  establishmentIds,
  statusFilter,
  releaseStatuses,
  types,
  onOpenDetail,
}) => {
  const { isDemoMode } = useDemoMode();
  const { trackButtonClick } = useAnalytics();
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [dueDateRange, billingPeriods, generatorId, establishmentIds, statusFilter, releaseStatuses, types]);

  const { handleExportCSV } = useInvoicesCSVExport({
    dueDateRange,
    billingPeriods,
    generatorIds: generatorId,
    establishmentIds,
    status: statusFilter,
    releaseStatuses,
    types,
  });

  const { data: response, isLoading, isFetching, isError } = useInvoices({
    page: currentPage,
    pageSize: ITEMS_PER_PAGE,
    dueDateRange,
    billingPeriods,
    generatorIds: generatorId,
    establishmentIds,
    status: statusFilter,
    releaseStatus: releaseStatuses?.length ? (releaseStatuses as ReleaseStatus[]) : undefined,
    types,
    // O extrato mostra também as medições em análise: é nelas que o gerador contesta. A
    // exportação usa a mesma flag e os mesmos filtros — planilha e tela são o mesmo recorte.
    includeInAnalysis: true,
  });

  const hasActiveFilters = !!(
    dueDateRange ||
    billingPeriods?.length ||
    generatorId?.length ||
    establishmentIds?.length ||
    (statusFilter && statusFilter !== 'all') ||
    releaseStatuses?.length ||
    types?.length
  );

  const invoices = response?.data ?? [];
  const pagination = response?.pagination;
  const totalItems = pagination?.count ?? 0;
  const totalPages = pagination?.last_page ?? 0;

  const startItem = totalItems > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0;
  const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

  // Refetch (troca de filtro ou de página) também mostra skeleton: os dados em
  // tela são de outro filtro, e escurecer a tabela sem indicador de progresso
  // parece a página travada.
  const isTableLoading = isLoading || isFetching;

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  return (
    <Card className="overflow-hidden rounded-2xl border-neutral-200 shadow-sm">
      <CardHeader>
        <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:justify-between sm:items-center">
          <CardTitle>
            <Text as="span" variant="heading" size="large" className="text-neutral-900">
              Extrato financeiro
            </Text>
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {!isTableLoading && (
              <Text variant="body" size="small" className="text-neutral-500">
                {hasActiveFilters
                  ? `Mostrando ${totalItems} ${totalItems === 1 ? 'registro encontrado' : 'registros encontrados'}`
                  : `Mostrando ${startItem}–${endItem} de ${totalItems} registros`}
              </Text>
            )}
            {!isDemoMode && (
              <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={isTableLoading || totalItems === 0}>
                <Download className="h-4 w-4 mr-2" />
                Exportar CSV
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-0">
        {isTableLoading ? (
          <TableSkeleton rows={ITEMS_PER_PAGE} />
        ) : isError ? (
          <div className="text-center py-8 px-6">
            <AlertCircle className="w-12 h-12 text-error-400 mx-auto mb-4" />
            <Text variant="body" className="text-neutral-500">Erro ao carregar faturas. Tente novamente.</Text>
          </div>
        ) : invoices.length === 0 ? (
          /* Duas ausências diferentes, duas leituras diferentes. Com filtro ativo o extrato
             existe e o recorte é que não trouxe nada; sem filtro nenhum, o gerador ainda não
             tem medição fechada — e dizer "para os filtros aplicados" ali manda procurar um
             filtro que não existe. Era o que a tela fazia antes de mostrar a listagem vazia
             nesse caso, quando a página inteira era substituída por um aviso de construção. */
          <div className="text-center py-8 px-6">
            <FileText className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            {hasActiveFilters ? (
              <>
                <Text variant="body" className="text-neutral-500">Nenhuma fatura encontrada para os filtros aplicados.</Text>
                <Text variant="body" size="small" className="text-neutral-500 mt-1 block">
                  Ajuste o período ou limpe os filtros para ver o restante do extrato.
                </Text>
              </>
            ) : (
              <>
                <Text variant="body" className="text-neutral-500 block max-w-md mx-auto">
                  Você ainda não tem medições ou faturas. Elas aparecem aqui assim que o primeiro
                  ciclo for fechado.
                </Text>
                <Button
                  variant="link"
                  onClick={openCloudHumansChat}
                  className="mt-2 h-auto p-0 font-medium"
                >
                  Falar com o suporte
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="px-4 md:px-6">
            {/* São nove colunas. Sem `min-w` na tabela o navegador comprime as
                células em vez de acionar o scroll do contêiner, e o conteúdo
                quebra: CNPJ em duas linhas, rótulo estourando a pílula de
                status, cabeçalho "Ver mais" partido. Com a largura mínima, a
                tabela rola na horizontal e cada coluna mantém a sua medida. */}
            <div className="bg-white rounded-lg border border-neutral-200 overflow-x-auto">
              <DataTable className="min-w-[1100px]">
                <DataTableHeader>
                  <DataTableRow>
                    <DataTableHead className="whitespace-nowrap">Nº</DataTableHead>
                    <DataTableHead className="whitespace-nowrap">Tipo</DataTableHead>
                    <DataTableHead className="whitespace-nowrap">Competência</DataTableHead>
                    <DataTableHead className="min-w-[220px]">Estabelecimento</DataTableHead>
                    <DataTableHead className="whitespace-nowrap">CNPJ</DataTableHead>
                    <DataTableHead className="whitespace-nowrap text-right">Valor total</DataTableHead>
                    <DataTableHead className="whitespace-nowrap">Vencimento</DataTableHead>
                    {/* `w-px`: truque de table-auto — vira "o mínimo que o conteúdo pedir" em vez de
                        esticar com a sobra da largura, que ia parar no vão entre Status e Ver mais. */}
                    <DataTableHead className="w-px whitespace-nowrap">Status</DataTableHead>
                    <DataTableHead className="w-px whitespace-nowrap text-center">Ver mais</DataTableHead>
                  </DataTableRow>
                </DataTableHeader>
                <DataTableBody>
                  {invoices.map((fatura) => (
                    <DataTableRow key={fatura.sfm_id}>
                      <DataTableCell className="font-medium whitespace-nowrap">{fatura.numero_pedido ?? fatura.sfm_id}</DataTableCell>
                      <DataTableCell className="whitespace-nowrap">
                        <Badge
                          variant={isInvoiced(fatura) ? 'info' : 'default'}
                          className="whitespace-nowrap"
                        >
                          {isInvoiced(fatura) ? 'Fatura' : 'Medição'}
                        </Badge>
                      </DataTableCell>
                      <DataTableCell className="whitespace-nowrap">{formatCompetencia(fatura.billing_period)}</DataTableCell>
                      <DataTableCell className="min-w-[220px]">{fatura.paying_generator ? maskGeneratorName(fatura.paying_generator.name, isDemoMode) : '—'}</DataTableCell>
                      <DataTableCell className="whitespace-nowrap tabular-nums">{fatura.paying_generator ? (isDemoMode ? maskCNPJ(fatura.paying_generator.document, isDemoMode) : formatCnpj(fatura.paying_generator.document)) : '—'}</DataTableCell>
                      <DataTableCell className="font-semibold text-neutral-900 text-right whitespace-nowrap tabular-nums">{formatCurrency(maskNumericValue(fatura.value, isDemoMode))}</DataTableCell>
                      <DataTableCell className="whitespace-nowrap tabular-nums">
                        <VencimentoCell invoice={fatura} />
                      </DataTableCell>
                      <DataTableCell className="w-px whitespace-nowrap">
                        {fatura.status ? (
                          <InvoiceStatusBadge status={fatura.status} />
                        ) : fatura.release_status ? (
                          <MeasurementStatusBadge
                            releaseStatus={fatura.release_status}
                            awaitingPo={fatura.awaiting_po}
                          />
                        ) : (
                          '—'
                        )}
                      </DataTableCell>
                      <DataTableCell className="w-px">
                        <div className="flex justify-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label="Ver detalhes da fatura"
                            onClick={() => {
                              trackButtonClick({
                                buttonLabel: 'financeiro_visualizar_fatura',
                                fatura_id: fatura.sfm_id,
                              });
                              onOpenDetail(fatura.sfm_id);
                            }}
                          >
                            <SquareArrowOutUpRight className="h-4 w-4 text-neutral-500" />
                          </Button>
                        </div>
                      </DataTableCell>
                    </DataTableRow>
                  ))}
                </DataTableBody>
              </DataTable>
            </div>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center mt-6 px-4 md:px-6 pb-4 md:pb-6">
            <DataPagination
              page={currentPage}
              pageSize={ITEMS_PER_PAGE}
              total={totalItems}
              onChange={setCurrentPage}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
